import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowDown, ArrowUpRight, Plus, Minus } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { breadcrumbSchema, faqSchema, serviceSchema } from "@/lib/seo";
import { CorporateQuoteForm } from "@/components/business/CorporateQuoteForm";
import { FeaturedWork } from "@/components/business/FeaturedWork";
import { PACKAGES, FIRESIDE, TIERS, TIER_ROWS, tierPrice, INDIVIDUAL, INCLUDED, QUOTED, STEPS, FAQS, KIT_PHOTO, KIT_DETAIL_PHOTO, ORNAMENT } from "@/components/business/corporateData";

const ease = [0.22, 1, 0.36, 1];
const Mono = ({ children, className = "" }) => <p className={`font-mono-tech text-[10px] uppercase tracking-[0.3em] ${className}`}>{children}</p>;
const Cell = ({ children, className = "" }) => <div className={`border border-zinc-800 -ml-px -mt-px ${className}`}>{children}</div>;
const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

const QuoteBtn = ({ pkg, label, testid, primary }) => (
  <button
    type="button" data-testid={testid}
    onClick={() => { window.dispatchEvent(new CustomEvent("corp-preset", { detail: pkg })); scrollTo("corporate-quote"); }}
    className={`w-full sm:w-auto justify-center inline-flex items-center gap-3 px-6 py-4 font-mono-tech uppercase tracking-[0.2em] text-[11px] transition-colors ${primary ? "bg-[#C87455] text-black hover:bg-[#A65B3E]" : "border border-zinc-700 text-zinc-200 hover:border-[#C87455] hover:text-[#C87455]"}`}
  >
    {label} <ArrowRight size={13} />
  </button>
);

export default function Business() {

  return (
    <main className="min-h-screen bg-[#070707]" data-testid="business-page">
      <Seo
        title="Corporate Gifting & Employee Gift Packages — Edmonton & Leduc | Ember & Steel Studio"
        description="Logo-engraved corporate gift packages from $49 per recipient: The Holiday Desk Drop, The Foreman, The Partner, The Executive and the Fireside cocktail smoker set. Volume pricing for Edmonton-area businesses, personalised in Leduc, Alberta."
        path="/business"
        image={KIT_PHOTO}
        jsonLd={[
          serviceSchema({ name: "Corporate Gifting & Employee Recognition Packages", description: "Coordinated, logo-engraved corporate gift packages and volume pricing for Edmonton-area businesses.", path: "/business", serviceType: "Corporate gifting" }),
          faqSchema(FAQS),
          breadcrumbSchema([["Home", "/"], ["Corporate Gifting", "/business"]]),
        ]}
      />
      <button type="button" onClick={() => scrollTo("christmas")} data-testid="corp-promo-banner" className="w-full mt-20 bg-[#C87455] text-black py-3 px-4 text-center font-mono-tech text-[10px] md:text-[11px] uppercase tracking-[0.25em] hover:bg-[#A65B3E] transition-colors">
        Corporate Christmas Collection 2026 — orders now open · reserve a production window <ArrowDown size={11} className="inline ml-1 -mt-0.5" />
      </button>

      <section className="relative pt-14 pb-14 lg:pt-24 border-b border-zinc-800" data-testid="b2b-hero-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-12 gap-0">
          <div className="col-span-12 lg:col-span-8 lg:pr-16">
            <Mono className="text-[#C87455]">[ Corporate Gifting · Leduc, Alberta ]</Mono>
            <h1 className="font-serif-display font-light text-white leading-[0.95] tracking-tight mt-8 text-5xl sm:text-6xl lg:text-[6.5rem]">
              {["Corporate gifts", "worth keeping."].map((w, i) => (
                <span key={w} className="block overflow-hidden">
                  <motion.span initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 1, delay: 0.15 + i * 0.12, ease }} className="block">{w}</motion.span>
                </span>
              ))}
            </h1>
            <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.5, ease }} className="text-zinc-300 text-base md:text-lg max-w-2xl mt-10 leading-relaxed">
              Recognize your team, thank your clients and celebrate the relationships behind your business. Ember &amp; Steel Studio creates personalized drinkware, executive gifts and curated gift boxes in Leduc, Alberta.
            </motion.p>
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.65, duration: 0.8 }} className="text-zinc-500 max-w-2xl mt-5 leading-relaxed">
              Choose a coordinated package or build a custom order around your budget. Company logos, recipient names and thoughtful presentation make every gift personal.
            </motion.p>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.75, duration: 0.8 }} className="flex flex-col sm:flex-row gap-3 mt-12">
              <QuoteBtn pkg="" label="Request a Corporate Quote" testid="btn-corporate-quote" primary />
              <button type="button" data-testid="btn-explore-packages" onClick={() => scrollTo("christmas")} className="w-full sm:w-auto justify-center inline-flex items-center gap-3 border border-zinc-700 text-zinc-200 hover:border-[#C87455] hover:text-[#C87455] transition-colors px-6 py-4 font-mono-tech uppercase tracking-[0.2em] text-[11px]">
                Explore Gift Packages <ArrowDown size={13} />
              </button>
            </motion.div>
          </div>
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.35, ease }} className="col-span-12 lg:col-span-4 mt-14 lg:mt-0 self-start">
            <button type="button" onClick={() => scrollTo("fireside")} data-testid="hero-fireside-card" className="group block w-full text-left border border-zinc-800 hover:border-[#C87455]/60 bg-[#0C0C0C] transition-colors duration-500">
              <div className="overflow-hidden">
                <img src={KIT_PHOTO} alt="Fireside personalized cocktail smoker set — engraved presentation box" className="w-full aspect-square object-cover transition-transform duration-700 group-hover:scale-[1.03]" data-testid="hero-fireside-photo" />
              </div>
              <div className="p-5 flex items-end justify-between gap-4">
                <div>
                  <Mono className="text-[#C87455]">Featured · Fireside</Mono>
                  <p className="font-serif-display text-xl text-white mt-2 leading-tight">Personalized cocktail smoker set</p>
                </div>
                <p className="font-mono-tech text-sm text-zinc-300 whitespace-nowrap">from ${FIRESIDE.price}</p>
              </div>
            </button>
          </motion.div>
        </div>
      </section>

      <section id="fireside" className="border-b border-zinc-800 bg-[#0C0C0C] scroll-mt-24" data-testid="corp-fireside-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="border border-zinc-800 overflow-hidden">
              <img src={KIT_PHOTO} alt="Fireside cocktail smoker set — engraved presentation box with smoker, whiskey stones, glasses and torch nested in foam" className="w-full object-cover aspect-square" data-testid="fireside-photo" />
            </div>
            <div className="border border-t-0 border-zinc-800 overflow-hidden">
              <img src={KIT_DETAIL_PHOTO} alt="Fireside detail — engraved wooden smoker top hat and monogrammed stainless-steel whiskey stones" loading="lazy" className="w-full object-cover aspect-[16/9]" data-testid="fireside-detail-photo" />
            </div>
            <Mono className="text-zinc-500 mt-3 normal-case tracking-[0.12em]">Styled presentation showing box, smoker and stone engraving. Your logo replaces ours. Final kit contents and engraving areas confirmed with your quote.</Mono>
          </div>
          <div className="lg:col-span-7">
            <div className="flex flex-wrap items-center gap-3">
              <span className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#C87455] border border-[#C87455]/40 px-2.5 py-1">{FIRESIDE.badge}</span>
              <span className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-amber-400 border border-amber-500/40 px-2.5 py-1" data-testid="fireside-preorder-badge">Enquire / Pre-order</span>
            </div>
            <h2 className="font-serif-display font-light text-3xl md:text-5xl text-white tracking-tight mt-6">{FIRESIDE.name}</h2>
            <p className="font-mono-tech text-sm text-zinc-300 mt-3" data-testid="fireside-price">From <span className="text-white text-xl">$119</span> CAD · <span className="text-[#C87455]">Box engraving is included.</span></p>
            <p className="text-zinc-400 text-sm leading-relaxed mt-5">{FIRESIDE.desc}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 pl-px pt-px mt-8">
              {FIRESIDE.options.map((o) => (
                <Cell key={o.key} className="p-5 bg-[#070707]" >
                  <div data-testid={`fireside-opt-${o.key}`} className="flex items-baseline justify-between gap-3">
                    <h4 className="font-serif-display text-xl text-white">{o.label}</h4>
                    <span className="font-mono-tech text-sm text-[#C87455] shrink-0">{o.total ? `$${o.total} CAD${o.key === "full" ? " total" : ""}` : `add $${o.add} CAD`}</span>
                  </div>
                  <ul className="mt-3 space-y-1.5">{o.lines.map((l) => <li key={l} className="text-xs text-zinc-400 flex gap-2"><span className="text-zinc-600">—</span>{l}</li>)}</ul>
                </Cell>
              ))}
            </div>
            <ul className="mt-6 space-y-1.5">{FIRESIDE.notes.map((n) => <li key={n} className="font-mono-tech text-[10px] text-zinc-500 uppercase tracking-wider">{n}</li>)}</ul>
            <div className="mt-8"><QuoteBtn pkg="fireside" label={FIRESIDE.cta} testid="btn-quote-fireside" primary /></div>
          </div>
        </div>
      </section>

      <section id="christmas" className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 scroll-mt-24" data-testid="corp-christmas-section">
        <Reveal>
          <Mono className="text-[#C87455]">Section 01</Mono>
          <h2 className="font-serif-display font-light text-3xl md:text-5xl text-white tracking-tight mt-4">Corporate Christmas Collection 2026</h2>
          <p className="text-zinc-400 mt-4 max-w-2xl">Thoughtfully selected gifts, personalized for the people behind your business. Prices include the standard personalization listed; additional customization is quoted separately.</p>
        </Reveal>
        <div className="grid grid-cols-1 lg:grid-cols-2 pl-px pt-px mt-10">
          {PACKAGES.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.06}>
              <Cell className="h-full bg-[#0C0C0C] hover:border-[#C87455]/60 transition-colors duration-500 flex flex-col overflow-hidden" >
                <div className="relative overflow-hidden border-b border-zinc-800 group">
                  <img src={p.photo} alt={`${p.name} corporate gift package — styled presentation`} loading="lazy" className="w-full aspect-[16/10] object-cover transition-transform duration-700 group-hover:scale-[1.03]" data-testid={`pkg-photo-${p.id}`} />
                  <span className="absolute bottom-3 left-4 font-mono-tech text-[9px] uppercase tracking-[0.25em] text-zinc-400 bg-black/60 backdrop-blur px-2 py-1">Styled presentation · contents as listed</span>
                </div>
                <div className="p-6 md:p-8 flex flex-col flex-1">
                <div data-testid={`pkg-${p.id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <span className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#C87455] border border-[#C87455]/40 px-2.5 py-1">{p.badge}</span>
                    <span className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-zinc-500">Quote-based</span>
                  </div>
                  <div className="flex items-baseline justify-between gap-4 mt-6">
                    <h3 className="font-serif-display text-3xl md:text-4xl text-white">{p.name}</h3>
                    <p className="font-mono-tech text-sm text-zinc-300 shrink-0" data-testid={`pkg-price-${p.id}`}>from <span className="text-white text-xl">${p.price}</span> CAD</p>
                  </div>
                  <p className="text-zinc-400 text-sm leading-relaxed mt-4">{p.desc}</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                    <div>
                      <Mono className="text-zinc-500 mb-3">Contents</Mono>
                      <ul className="space-y-1.5">{p.contents.map((c) => <li key={c} className="text-sm text-zinc-300 flex gap-2"><span className="text-[#C87455]">◆</span>{c}</li>)}</ul>
                    </div>
                    <div>
                      <Mono className="text-zinc-500 mb-3">Included personalization</Mono>
                      <ul className="space-y-1.5">{p.personalization.map((c) => <li key={c} className="text-sm text-zinc-400 flex gap-2"><span className="text-zinc-600">—</span>{c}</li>)}</ul>
                    </div>
                  </div>
                  {p.note && <p className="font-mono-tech text-[10px] text-zinc-500 mt-5 uppercase tracking-wider">{p.note}</p>}
                </div>
                <div className="mt-auto pt-6"><QuoteBtn pkg={p.id} label={p.cta} testid={`btn-quote-${p.id}`} /></div>
                </div>
              </Cell>
            </Reveal>
          ))}
        </div>
        <p className="font-mono-tech text-[10px] text-zinc-500 mt-6 uppercase tracking-wider">Corporate packages are personalized to order and quoted — not stocked for immediate shipment. No affiliation with or endorsement by YETI is implied.</p>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20" data-testid="corp-individual-section">
        <Reveal>
          <Mono className="text-[#C87455]">Section 03</Mono>
          <h2 className="font-serif-display font-light text-3xl md:text-5xl text-white tracking-tight mt-4">Build a gift around your business.</h2>
          <p className="text-zinc-400 mt-4 max-w-2xl">Choose one memorable item or combine complementary pieces. We'll help you build an order around your audience, budget and required date.</p>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 pl-px pt-px mt-10">
          {INDIVIDUAL.map(([label, price, to]) => {
            const inner = (<><p className="text-sm text-white leading-snug">{label}</p><p className="font-mono-tech text-[10px] text-zinc-500 mt-3 uppercase tracking-wider">{price}</p></>);
            const cls = "block p-5 h-full bg-[#0C0C0C] hover:border-[#C87455]/60 transition-colors";
            return (
              <Cell key={label} className="">
                {to && to.startsWith("/") ? <Link to={to} className={cls} data-testid={`ind-${to.split("/").pop()}`}>{inner}<ArrowUpRight size={12} className="text-zinc-600 mt-3" /></Link>
                  : <button type="button" onClick={() => (to ? scrollTo("fireside") : (window.dispatchEvent(new CustomEvent("corp-preset", { detail: "" })), scrollTo("corporate-quote")))} className={`${cls} w-full text-left`}>{inner}</button>}
              </Cell>
            );
          })}
        </div>
      </section>

      <section className="border-y border-zinc-800 bg-[#0C0C0C]" data-testid="corp-pricing-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
          <Mono className="text-[#C87455]">Corporate quantity pricing · per complete gift · CAD before GST and delivery</Mono>
          <div className="overflow-x-auto mt-6 -mx-6 px-6">
            <table className="w-full min-w-[520px] text-left" data-testid="corp-pricing-table">
              <thead>
                <tr className="border-b border-zinc-800 font-mono-tech text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                  <th className="py-3 pr-4 font-normal">Product</th>
                  {TIERS.map((t) => <th key={t.range} className="py-3 pr-4 font-normal">{t.range}<span className="block text-zinc-600 normal-case tracking-normal">{t.label}</span></th>)}
                  <th className="py-3 font-normal">50+</th>
                </tr>
              </thead>
              <tbody>
                {TIER_ROWS.map(([name, base]) => (
                  <tr key={name} className="border-b border-zinc-900" data-testid={`tier-row-${name.toLowerCase().replace(/\s+/g, "-")}`}>
                    <td className="py-3 pr-4 text-sm text-white">{name}</td>
                    {TIERS.map((t) => <td key={t.range} className="py-3 pr-4 font-mono-tech text-sm text-zinc-300">${tierPrice(base, t.factor)}</td>)}
                    <td className="py-3 font-mono-tech text-[10px] uppercase tracking-wider text-[#C87455]">Custom quote</td>
                  </tr>
                ))}
                <tr data-testid="tier-row-fireside-fully-personalized">
                  <td className="py-3 pr-4 text-sm text-white">Fireside Fully Personalized <span className="font-mono-tech text-[10px] text-zinc-500 block">base discounted, then +$30 upgrades</span></td>
                  {TIERS.map((t) => <td key={t.range} className="py-3 pr-4 font-mono-tech text-sm text-zinc-300">${tierPrice(119, t.factor, 30)}</td>)}
                  <td className="py-3 font-mono-tech text-[10px] uppercase tracking-wider text-[#C87455]">Custom quote</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-zinc-500 text-sm mt-6 max-w-3xl leading-relaxed">One coordinated order with a shared company logo, production window and delivery destination. Mixed package quantities may qualify together. Delivery, rush work and separately quoted upgrades are excluded. Discounts do not stack. Fireside upgrades (top hat +$15 · stones +$20 · both +$30) are added after discounting the base kit and are not discounted again.</p>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 grid grid-cols-1 lg:grid-cols-2 gap-12" data-testid="corp-personalization-section">
        <div>
          <Mono className="text-[#C87455]">Included personalization</Mono>
          <ul className="mt-5 space-y-2">{INCLUDED.map((x) => <li key={x} className="text-sm text-zinc-300 flex gap-3"><span className="text-[#C87455]">◆</span>{x}</li>)}</ul>
          <p className="text-zinc-500 text-sm mt-5">Simple personalization is included in the package prices. {ORNAMENT}: two wooden layers in a standard outer shape, company logo engraved on the front, optional first name and year where space permits, hanging cord or ribbon. Exact brand-colour matching is not possible through laser engraving.</p>
        </div>
        <div>
          <Mono className="text-zinc-500">Quoted separately</Mono>
          <ul className="mt-5 space-y-2">{QUOTED.map((x) => <li key={x} className="text-sm text-zinc-400 flex gap-3"><span className="text-zinc-600">—</span>{x}</li>)}</ul>
        </div>
      </section>

      <FeaturedWork />

      <section className="border-y border-zinc-800 bg-[#0C0C0C]" data-testid="corp-ordering-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
          <h2 className="font-serif-display font-light text-2xl md:text-4xl text-white tracking-tight">Christmas 2026 orders are now open.</h2>
          <p className="text-zinc-400 mt-3 max-w-3xl">Availability depends on product selection, quantity and your required delivery date. Contact us early to reserve a production window.</p>
          <p className="text-zinc-500 text-sm mt-2 max-w-3xl">Your production and delivery schedule will be confirmed in writing after materials and artwork are approved. Shipping and delivery are quoted separately.</p>
          <div className="grid grid-cols-1 sm:grid-cols-5 pl-px pt-px mt-8">
            {STEPS.map((s, i) => (
              <Cell key={s} className="p-5">
                <p className="font-mono-tech text-3xl text-zinc-700">0{i + 1}</p>
                <p className="text-sm text-zinc-300 mt-3 leading-snug">{s}</p>
              </Cell>
            ))}
          </div>
        </div>
      </section>

      <section id="corporate-quote" className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 grid grid-cols-1 lg:grid-cols-12 gap-12 scroll-mt-24" data-testid="corp-quote-section">
        <div className="lg:col-span-4">
          <Mono className="text-[#C87455]">[ Corporate quote ]</Mono>
          <h2 className="font-serif-display font-light text-4xl md:text-5xl text-white tracking-tight leading-[0.95] mt-5">Request a Corporate Quote</h2>
          <p className="text-zinc-400 mt-6 leading-relaxed">Tell us which packages, how many and when you need them in hand. We'll confirm availability and reply with a tailored quotation.</p>
          <p className="font-mono-tech text-xs text-zinc-500 mt-8">sales@embersteelstudio.ca</p>
        </div>
        <div className="lg:col-span-8"><CorporateQuoteForm /></div>
      </section>

      <section className="border-t border-zinc-800" data-testid="corp-faq-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
          <Mono className="text-[#C87455]">Corporate FAQs</Mono>
          <div className="mt-6 border-t border-zinc-800">{FAQS.map(([q, a], i) => <Faq key={q} q={q} a={a} i={i} />)}</div>
        </div>
      </section>
    </main>
  );
}

const Faq = ({ q, a, i }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-zinc-800">
      <button type="button" data-testid={`faq-${i}`} onClick={() => setOpen(!open)} className="w-full flex items-center justify-between gap-6 py-5 text-left">
        <span className="font-serif-display text-xl md:text-2xl text-white">{q}</span>
        {open ? <Minus size={16} className="text-[#C87455] shrink-0" /> : <Plus size={16} className="text-zinc-500 shrink-0" />}
      </button>
      {open && <p className="text-zinc-400 text-sm leading-relaxed pb-6 max-w-3xl" data-testid={`faq-answer-${i}`}>{a}</p>}
    </div>
  );
};
